import fetcher from "fetcher";
import base from "./base";
import { cache } from "react";
import { unstable_cache } from "next/cache";
export const revalidate = 60; // Default revalidation for non-book data

// Books revalidate faster (15s)
const BOOKS_REVALIDATE_TIME = 15;
const DEFAULT_REVALIDATE_TIME = 60;

const buildAuthHeaders = (cookieStore) => {
  if (!cookieStore) return undefined;
  const cookieHeader =
    typeof cookieStore === "string"
      ? cookieStore
      : typeof cookieStore?.toString === "function"
        ? cookieStore.toString()
        : "";
  if (!cookieHeader) return undefined;
  return { cookie: cookieHeader };
};

export const getStatistics = async () => {
  try {
    const result = await fetcher(`${base.apiUrl}/gerelt/statistics`);
    return { statistics: result.data };
  } catch (error) {
    return { error };
  }
};

export const getAppInfo = async () => {
  try {
    const result = await fetcher(`${base.apiUrl}/appinfo?limit=1`);
    return { appInfo: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSocialLinks = async (query) => {
  try {
    if (query) {
      const result = await fetcher(`${base.apiUrl}/slinks?${query}`);
      return { socials: result.data };
    }

    const result = await fetcher(`${base.apiUrl}/webinfo`);
    return { socials: result?.data?.socials || [] };
  } catch (error) {
    return { error };
  }
};
export const getServicies = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/service?${query}`);
    return { servicies: result.data };
  } catch (error) {
    return { error };
  }
};

export const getService = async (id) => {
  try {
    const result = await fetcher(`${base.apiUrl}/servicies/${id}`);
    return { service: result.data };
  } catch (error) {
    return { error };
  }
};

export const getProject = async (id) => {
  try {
    const result = await fetcher(`${base.apiUrl}/projects/${id}`);
    return { project: result.data };
  } catch (error) {
    return { error };
  }
};

export const getUpBanner = async () => {
  try {
    const result = await fetcher(`${base.apiUrl}/adsies?limit=1`);
    return { ads: result.data[0] };
  } catch (err) {
    return { err };
  }
};

const normalizeWebInfoPayload = (payload) => {
  if (!payload) return payload;
  if (payload?.success === true && payload?.data !== undefined)
    return payload.data;
  return payload;
};

const normalizeLangForApi = (lang) => {
  if (!lang) return null;
  const normalized = String(lang).toLowerCase().trim();
  if (normalized === "eng") return "en";
  return normalized;
};

const hasValue = (value) =>
  value !== undefined &&
  value !== null &&
  value !== "undefined" &&
  value !== "";

const getWebInfoInternal = async (lang) => {
  try {
    const coreResult = await fetcher(`${base.apiUrl}/webinfo`);
    const corePayload = normalizeWebInfoPayload(coreResult?.data);

    const requestedLang = normalizeLangForApi(lang);
    if (
      requestedLang &&
      corePayload?.translation &&
      String(corePayload.translation.language).toLowerCase() === requestedLang
    ) {
      return { info: corePayload };
    }

    if (requestedLang) {
      try {
        const tResult = await fetcher(
          `${base.apiUrl}/webinfo/lang/${requestedLang}`,
        );
        const translation = normalizeWebInfoPayload(tResult?.data);
        if (translation && corePayload && typeof corePayload === "object") {
          const fallbackTranslation =
            corePayload?.translation &&
            typeof corePayload.translation === "object"
              ? corePayload.translation
              : null;
          const mergedTranslation = fallbackTranslation
            ? { ...fallbackTranslation, ...translation }
            : translation;

          if (fallbackTranslation) {
            ["logo", "whiteLogo"].forEach((key) => {
              if (
                !hasValue(mergedTranslation[key]) &&
                hasValue(fallbackTranslation[key])
              ) {
                mergedTranslation[key] = fallbackTranslation[key];
              }
            });
          }

          return { info: { ...corePayload, translation: mergedTranslation } };
        }
        return { info: translation };
      } catch (error) {
        return { info: corePayload };
      }
    }

    return { info: corePayload };
  } catch (error) {
    return { error };
  }
};

export const getWebInfo =
  typeof window === "undefined"
    ? cache(getWebInfoInternal)
    : getWebInfoInternal;

// Cache-гүй хувилбар - metadata-д ашиглах
export const getWebInfoNoCache = getWebInfoInternal;

// Google Analytics gCode (from DB WebIntegration)
export const getGoogleAnalyticsCode = async () => {
  try {
    const result = await fetcher(`${base.apiUrl}/webinfo/ga-code`);
    // Controller returns: { success: true, data: { gCode: 'G-XXXX' } }
    const payload = result?.data || result;
    const gCode =
      payload?.data?.gCode ?? payload?.gCode ?? payload?.code ?? null;
    return { gCode: typeof gCode === "string" ? gCode.trim() : null };
  } catch (error) {
    return { gCode: null, error };
  }
};

export const getTourCategories = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/tourcategories?${query}`);
    return { categories: result.data };
  } catch (error) {
    return { error };
  }
};

export const getMembers = async (query) => {
  try {
    const suffix = query ? `?${query}` : "";
    const result = await fetcher(`${base.apiUrl}/banners${suffix}`);
    return { banners: result.data, pagination: result.pagination };
  } catch (error) {
    return { error, banners: [] };
  }
};

export const getSlugPage = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/webpages/${slug}`);

    return {
      page: result.data,
    };
  } catch (error) {
    return { error };
  }
};

export const getSlugMenu = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/menus/slug/${slug}`);
    return {
      menu: result.data,
      parent: result.parent,
      page: result.page,
      childeMenus: result.childeMenus,
      sameParentMenus: result.sameParentMenus,
      position: result.position,
    };
  } catch (error) {
    return { error };
  }
};

export const getBreadcrumbs = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/menus/breadcrumb/${slug}`);
    return { breadcrumbs: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSlugSiteContent = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/site-contents/slug/${slug}`);
    return {
      content: result.data,
    };
  } catch (error) {
    return { error };
  }
};

export const getSlugFooterMenu = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/footermenu/slug/${slug}`);

    return {
      menu: result.data,
      parent: result.parent,
      page: result.page,
      childeMenus: result.childeMenus,
      sameParentMenus: result.sameParentMenus,
      position: result.position,
    };
  } catch (error) {
    return { error };
  }
};

export const getMenus = async (query) => {
  try {
    const suffix = query ? `?${query}` : "";
    const result = await fetcher(`${base.apiUrl}/menus/taxonomy/tree${suffix}`);
    return { menus: result.data };
  } catch (error) {
    return { error };
  }
};

export const getFooterMenus = async () => {
  try {
    const result = await fetcher(`${base.apiUrl}/footermenu?status=true`);
    return { menus: result.data };
  } catch (error) {
    return { error };
  }
};

export const getBanners = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/banners?${query}`);
    return { banners: result.data };
  } catch (error) {
    return { error };
  }
};

export const getAuthors = async (query, cookieStore) => {
  try {
    const suffix = query ? `?${query}` : "";
    const headers = buildAuthHeaders(cookieStore);
    const options = headers ? { cache: "no-cache", headers } : undefined;
    const result = await fetcher(
      `${base.apiUrl}/gerelt_authors${suffix}`,
      options,
    );
    return { authors: result.data };
  } catch (error) {
    return { error };
  }
};

export const getCompanies = async (query, cookieStore) => {
  try {
    const suffix = query ? `?${query}` : "";
    const headers = buildAuthHeaders(cookieStore);
    const options = headers ? { cache: "no-cache", headers } : undefined;
    const result = await fetcher(
      `${base.apiUrl}/gerelt_companies${suffix}`,
      options,
    );
    return { companies: result.data };
  } catch (error) {
    return { error };
  }
};

export const getNews = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/news?${query}`);

    return { news: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getYearNews = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/news/year?${query}`);

    return { news: result.data, next: result.nextData };
  } catch (error) {
    return { error };
  }
};

export const getMeeting = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/meetings?${query}`);
    return { meetings: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getServices = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/service?${query}`);
    return { services: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const loadCounter = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/static?${query}`);

    return { counters: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getSlugNews = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/news/slug/${slug}`);

    return { news: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSlugTour = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/tours/slug/${slug}`);

    return { tour: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSlugCar = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/carrents/slug/${slug}`);

    return { car: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSlugDestinations = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/destinations/slug/${slug}`);
    return { destination: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSlugWork = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/work/slug/${slug}`);

    return { work: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSlugMeeting = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/meetings/slug/${slug}`);
    return { meeting: result.data };
  } catch (error) {
    return { error };
  }
};

export const getFaq = async (id) => {
  try {
    const result = await fetcher(`${base.apiUrl}/faqs/${id}`);
    return { faq: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSlugService = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/service/slug/${slug}`);
    return { service: result.data };
  } catch (error) {
    return { error };
  }
};

export const getEmployee = async (id) => {
  try {
    const result = await fetcher(`${base.apiUrl}/employees/${id}`);
    return { employee: result.data };
  } catch (error) {
    return { error };
  }
};

export const getNewsCategories = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/newscategories?${query}`);
    return { categories: result.data };
  } catch (error) {
    return { error };
  }
};

/** Podcast ангилал (taxonomy жагсаалт). Schema slug нь орчиндоо `podcastcategories` гэж тохируулсан гэж үзнэ. */
export const getPodcastCategories = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/podcastcategories?${query}`);
    return { categories: result.data };
  } catch (error) {
    return { error };
  }
};

export const getFileCategories = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/file-categories?${query}`);
    return { categories: result.data };
  } catch (error) {
    return { error };
  }
};

export const getIdNews = async (id) => {
  try {
    const result = await fetcher(`${base.apiUrl}/news/${id}`);
    return { news: result.data };
  } catch (error) {
    return { error };
  }
};

export const getTopLinks = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/toplinks?${query}`);
    return { toplinks: result.data };
  } catch (error) {
    return { error };
  }
};

export const getProjects = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/projects?${query}`);
    return { projects: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getProjectCategory = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/project-categories`);
    return { categories: result.data };
  } catch (error) {
    return { error };
  }
};

export const getFastLinks = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/fastlinks?${query}`);
    return { fastlinks: result.data };
  } catch (error) {
    return { error };
  }
};

export const getMedias = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/media?${query}`);

    return { medias: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

const _getBooksInternal = async (query, cookieStore) => {
  try {
    const headers = buildAuthHeaders(cookieStore);
    const options = headers ? { cache: "no-cache", headers } : undefined;
    const result = await fetcher(
      `${base.apiUrl}/gerelt_books?${query}`,
      options,
    );

    return { books: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getBooks = unstable_cache(_getBooksInternal, ["getBooks"], {
  revalidate: BOOKS_REVALIDATE_TIME,
  tags: ["books"],
});

const levenshteinDistance = (left, right) => {
  const a = String(left || "");
  const b = String(right || "");
  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let i = 0; i < rows; i += 1) matrix[i][0] = i;
  for (let j = 0; j < cols; j += 1) matrix[0][j] = j;

  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[a.length][b.length];
};

const slugSimilarity = (left, right) => {
  const a = String(left || "").toLowerCase();
  const b = String(right || "").toLowerCase();
  if (!a || !b) return 0;
  if (a === b) return 1;

  const distance = levenshteinDistance(a, b);
  return 1 - distance / Math.max(a.length, b.length);
};

const _getBookInternal = async (slug, cookieStore) => {
  const rawSlug = String(slug || "").trim();
  const safeSlug = encodeURIComponent(rawSlug);
  const headers = buildAuthHeaders(cookieStore);
  const options = headers ? { cache: "no-cache", headers } : { cache: "no-cache" };

  const candidates = [
    `${base.apiUrl}/gerelt_books/slug/${safeSlug}`,
    `${base.apiUrl}/gerelt_books/s/${safeSlug}`,
    `${base.apiUrl}/gerelt_books?slug=${safeSlug}&limit=1`,
  ];

  let lastError = null;
  for (const url of candidates) {
    try {
      const result = await fetcher(url, options);
      if (!result) continue;

      if (Array.isArray(result?.data)) {
        const exact = result.data.find(
          (item) => String(item?.slug || "").toLowerCase() === rawSlug.toLowerCase(),
        );
        if (exact) return { book: exact };
        const first = result.data[0] || null;
        if (first) return { book: first };
        continue;
      }

      if (result?.data) {
        return { book: result.data };
      }
    } catch (error) {
      lastError = error;
    }
  }

  try {
    const searchTerms = [rawSlug, rawSlug.replace(/-/g, " ")];
    for (const term of searchTerms) {
      const result = await fetcher(
        `${base.apiUrl}/gerelt_books?search=${encodeURIComponent(term)}&limit=8`,
        options,
      );

      if (!Array.isArray(result?.data) || result.data.length === 0) continue;

      const exact = result.data.find(
        (item) => String(item?.slug || "").toLowerCase() === rawSlug.toLowerCase(),
      );
      if (exact) return { book: exact };

      const ranked = result.data
        .filter((item) => item?.slug)
        .map((item) => ({
          item,
          score: slugSimilarity(rawSlug, item.slug),
        }))
        .sort((a, b) => b.score - a.score);

      const [bestMatch] = ranked;
      if (bestMatch && bestMatch.score >= 0.55) {
        return { book: bestMatch.item };
      }
    }
  } catch (error) {
    lastError = error;
  }

  return { book: null, error: lastError || new Error("Book not found") };
};

export const getBook = _getBookInternal;

const _getGroupedBooksInternal = async (query, cookieStore) => {
  try {
    const suffix = query ? `?${query}` : "";
    const headers = buildAuthHeaders(cookieStore);
    const options = headers ? { cache: "no-cache", headers } : undefined;
    const result = await fetcher(
      `${base.apiUrl}/gerelt_books/grouped${suffix}`,
      options,
    );
    return { groups: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getGroupedBooks = unstable_cache(
  _getGroupedBooksInternal,
  ["getGroupedBooks"],
  {
    revalidate: BOOKS_REVALIDATE_TIME,
    tags: ["books"],
  },
);

const _searchBooksInternal = async (query, cookieStore) => {
  try {
    const suffix = query ? `?${query}` : "";
    const headers = buildAuthHeaders(cookieStore);
    const options = headers ? { cache: "no-cache", headers } : undefined;
    const result = await fetcher(
      `${base.apiUrl}/gerelt_books/search${suffix}`,
      options,
    );
    return { books: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const searchBooks = unstable_cache(
  _searchBooksInternal,
  ["searchBooks"],
  {
    revalidate: BOOKS_REVALIDATE_TIME,
    tags: ["books"],
  },
);

export const getPartners = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/partners?${query}`);

    return { partners: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getServicePartners = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/service-partners?${query}`);

    return { partners: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getMediaCategories = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/media-categories?${query}`);
    return { categories: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSlugMedia = async (slug) => {
  try {
    const result = await fetcher(`${base.apiUrl}/media/s/${slug}`);

    return { media: result.data };
  } catch (error) {
    return { error };
  }
};

export const getSiteContents = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/site-contents?${query}`);
    return { contents: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getWorks = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/work?${query}`);
    return { works: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getTours = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/tours?${query}`);
    return { tours: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getTestimonials = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/testimonials?${query}`);
    return { testimonials: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getRents = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/carrents?${query}`);
    return { rents: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getDestinations = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/destinations?${query}`);
    return { destinations: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getTreeTypes = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/trees?${query}`);
    return { treeTypes: result.data };
  } catch (error) {
    return { error };
  }
};

export const getFiles = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/files?${query}`);
    return { files: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getFaqs = async (query) => {
  try {
    const result = await fetcher(`${base.apiUrl}/faqs?${query}`);
    return { faqs: result.data, pagination: result.pagination };
  } catch (error) {
    return { error };
  }
};

export const getNewsItem = async (slug, cookieStore) => {
  try {
    const headers = buildAuthHeaders(cookieStore);
    const options = headers
      ? { headers, cache: "no-store" }
      : { cache: "no-store" };
    const result = await fetcher(`${base.apiUrl}/news/${slug}`, options);
    return { news: result.data };
  } catch (error) {
    return { error };
  }
};

export const getPodcasts = async (query = "") => {
  try {
    const result = await fetcher(`${base.apiUrl}/podcast?${query}`);
    return { podcasts: result.data || [], pagination: result.pagination };
  } catch (error) {
    return { error, podcasts: [], pagination: null };
  }
};

export const getPodcastBySlug = async (slug) => {
  if (!slug) return { podcast: null };

  const safeSlug = encodeURIComponent(String(slug));
  const candidates = [
    `${base.apiUrl}/podcast/slug/${safeSlug}`,
    `${base.apiUrl}/podcast/s/${safeSlug}`,
    `${base.apiUrl}/podcast?slug=${safeSlug}&limit=1`,
  ];

  let lastError = null;

  for (const url of candidates) {
    try {
      const result = await fetcher(url);
      if (result?.notFound) continue;

      if (Array.isArray(result?.data)) {
        const first = result.data[0] || null;
        if (first) return { podcast: first };
        continue;
      }

      if (result?.data) {
        return { podcast: result.data };
      }
    } catch (error) {
      lastError = error;
    }
  }

  return { podcast: null, error: lastError };
};

export const getYouTubeVideos = async (query = "") => {
  try {
    const result = await fetcher(`${base.apiUrl}/youtubevideos?${query}`);
    return { videos: result.data || [], pagination: result.pagination };
  } catch (error) {
    return { error, videos: [], pagination: null };
  }
};

export const getHighlights = async (query = "status=true") => {
  try {
    const suffix = query ? `?${query}` : "";
    const result = await fetcher(`${base.apiUrl}/highlights${suffix}`);
    return { highlights: result.data || [], pagination: result.pagination };
  } catch (error) {
    return { error, highlights: [], pagination: null };
  }
};
